/**
 * Shared hotspot stimulus for bank items.
 *
 * Coronary / LAD items use the anterior heart photograph. Other hotspot
 * items keep the inlined chest figure so SPA hosts cannot rewrite a missing
 * asset to HTML.
 */

/** Percentages of the image box (`left` / `top` / `width` / `height`). */
export interface HotspotAreaPct {
  left: number
  top: number
  width: number
  height: number
}

export type HotspotFigure = {
  src: string
  alt: string
  imageLabel: string
  correctArea: HotspotAreaPct
  kind: "photo" | "figure"
}

export const HOTSPOT_STOCK: HotspotFigure = {
  /**
   * Legacy public path — kept for tests / older payloads. Prefer rendering
   * `HotspotStockFigure` (inlined) so SPA hosts cannot rewrite the asset to HTML.
   */
  src: "/hotspot/clinical-figure.svg",
  alt: "Anterior chest figure with heart silhouette",
  imageLabel: "Anterior chest figure",
  kind: "figure",
  /**
   * Apex of the heart on the shared figure. Same box for every generic
   * hotspot item; region labels still vary by topic.
   */
  correctArea: {
    left: 34,
    top: 48,
    width: 16,
    height: 22,
  },
}

/** Anterior heart with coronary arteries. Correct zone is the LAD course. */
export const HOTSPOT_CORONARY: HotspotFigure = {
  src: "/hotspot/coronary-heart.jpg",
  alt: "Anterior heart with coronary arteries",
  imageLabel: "Coronary arteries, anterior view",
  kind: "photo",
  /**
   * LAD in the anterior interventricular groove on the 787×1000 photograph.
   * Percentages are of the image, not a 16:9 letterbox.
   */
  correctArea: {
    left: 63,
    top: 50,
    width: 8,
    height: 32,
  },
}

const CORONARY_STEM =
  /anterior descending|left anterior descending|\blad\b|coronary diagram|coronary arter/i

export function isCoronaryHotspotStem(stem: string): boolean {
  return CORONARY_STEM.test(stem)
}

const SA_NODE_STEM =
  /sinoatrial|\bsa node\b|initiates the (cardiac )?impulse|conduction system/i

export function isSaNodeHotspotStem(stem: string): boolean {
  return SA_NODE_STEM.test(stem)
}

export const CORONARY_HOTSPOT_REGIONS = [
  { id: "region-A", label: "Left anterior descending artery", correct: true },
] as const

/** SA node on the shared chest figure (upper right atrium, viewer left). */
const HOTSPOT_SA_NODE: HotspotFigure = {
  ...HOTSPOT_STOCK,
  correctArea: {
    left: 35,
    top: 42,
    width: 12,
    height: 16,
  },
}

export type HotspotKeyRegion = {
  id: string
  label: string
  correct: boolean
}

/** Figure, hit box, and correct region for a stem. One source for compose, import, and preview. */
export function hotspotKeyForStem(stem: string): {
  figure: HotspotFigure
  regions: HotspotKeyRegion[]
} {
  if (isCoronaryHotspotStem(stem)) {
    return {
      figure: HOTSPOT_CORONARY,
      regions: CORONARY_HOTSPOT_REGIONS.map((region) => ({ ...region })),
    }
  }
  if (isSaNodeHotspotStem(stem)) {
    return {
      figure: HOTSPOT_SA_NODE,
      regions: [
        { id: "region-A", label: "Sinoatrial node", correct: true },
      ],
    }
  }
  return {
    figure: HOTSPOT_STOCK,
    regions: [{ id: "region-A", label: "Correct area", correct: true }],
  }
}

export function hotspotFigureFor(stem: string): HotspotFigure {
  return hotspotKeyForStem(stem).figure
}

export function hotspotComposeRegions(stem: string): {
  id: string
  shape: "rect"
  label: string
  isCorrect: boolean
  x: number
  y: number
  w: number
  h: number
}[] {
  const { figure, regions } = hotspotKeyForStem(stem)
  return regions
    .filter((region) => region.correct)
    .map((region) => ({
      id: region.id,
      shape: "rect" as const,
      label: region.label,
      isCorrect: true,
      x: figure.correctArea.left,
      y: figure.correctArea.top,
      w: figure.correctArea.width,
      h: figure.correctArea.height,
    }))
}
